export type DifficultyInfo = {
  rawScore: number | null
  level: number | null
  label: string
  shortLabel: string
}

const clampScore = (value: number): number => {
  if (Number.isNaN(value)) {
    return value
  }

  if (value < 0) {
    return 0
  }

  if (value > 1) {
    return 1
  }

  return value
}

export const parseDifficultyNumber = (value: any): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value
  }

  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (trimmed.length > 0) {
      const parsed = Number(trimmed)
      if (Number.isFinite(parsed)) {
        return parsed
      }
    }
  }

  return null
}

export const getLevelFromScore = (score: number): number => {
  const normalized = clampScore(score)

  if (normalized <= 0.2) {
    return 5
  }

  if (normalized <= 0.4) {
    return 4
  }

  if (normalized <= 0.6) {
    return 3
  }

  if (normalized <= 0.8) {
    return 2
  }

  return 1
}

export const getScoreRangeForLevel = (
  level: number | null | undefined
): {min: number; max: number} | null => {
  if (typeof level !== 'number' || !Number.isFinite(level)) {
    return null
  }

  const normalizedLevel = Math.min(Math.max(Math.round(level), 1), 5)

  switch (normalizedLevel) {
    case 1:
      return {min: 0.8, max: 1}
    case 2:
      return {min: 0.6, max: 0.8}
    case 3:
      return {min: 0.4, max: 0.6}
    case 4:
      return {min: 0.2, max: 0.4}
    case 5:
      return {min: 0, max: 0.2}
    default:
      return null
  }
}

export const deriveDifficultyInfo = (value: any): DifficultyInfo => {
  const numeric = parseDifficultyNumber(value)

  if (numeric === null) {
    return {
      rawScore: null,
      level: null,
      label: 'Belirtilmemiş',
      shortLabel: '-',
    }
  }

  const isScore = numeric >= 0 && numeric <= 1
  const rawScore = isScore ? clampScore(numeric) : null
  const level = isScore
    ? getLevelFromScore(rawScore ?? 0)
    : Math.min(Math.max(Math.round(numeric), 1), 5)

  const labelParts: string[] = []
  if (level) {
    labelParts.push(`Seviye ${level}`)
  }

  if (rawScore !== null) {
    labelParts.push(rawScore.toFixed(2))
  }

  const label = labelParts.length ? labelParts.join(' · ') : 'Belirtilmemiş'
  const shortLabel = labelParts.length ? labelParts[0] : '-'

  return {rawScore, level, label, shortLabel}
}

export const mapLevelBoundsToScoreBounds = (
  minLevel?: number | null,
  maxLevel?: number | null
): {minScore?: number; maxScore?: number} => {
  const minRange = getScoreRangeForLevel(minLevel ?? undefined)
  const maxRange = getScoreRangeForLevel(maxLevel ?? undefined)

  const bounds: {minScore?: number; maxScore?: number} = {}

  if (minRange) {
    bounds.minScore = minRange.min
  }

  if (maxRange) {
    bounds.maxScore = maxRange.max
  }

  return bounds
}
